import React, { FC } from "react";
import {
  Cell,
  Label,
  Pie,
  PieChart as RePieChart,
  ResponsiveContainer,
} from "recharts";
import { NO_DATA_COLOR } from "../../modules/statistics/constants";
import { IPieChartSegment } from "./types";
import PieChartCenterLabel from "./PieChartCenterLabel";

interface IPieChartProps {
  data: IPieChartSegment[];
  startAngle?: number;
  endAngle?: number;
  paddingAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  cx?: number;
  cy?: number;
}

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  color,
}) => {
  const RADIAN = Math.PI / 180;

  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={color}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${name} - ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieChart: FC<IPieChartProps> = ({
  data,
  startAngle = 90,
  endAngle = -270,
  paddingAngle = 2,
  innerRadius = 90,
  outerRadius = 110,
  cx = 384,
  cy = 165,
}) => {
  const total = data.reduce((acc, d) => acc + d.value, 0);
  const noData = [{ value: 1, color: NO_DATA_COLOR }];
  const actualData = total ? data : noData;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RePieChart>
        <Pie
          data={actualData}
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius}
          {...(total ? { label: renderCustomizedLabel } : {})}
          outerRadius={outerRadius}
          paddingAngle={total ? paddingAngle : 0}
          stroke="none"
          dataKey="value"
        >
          <Label
            width={30}
            position="center"
            content={<PieChartCenterLabel subTitle={"total"} total={total} />}
          ></Label>
          {actualData.map((entry, index) => (
            <Cell
              style={{ outline: "none" }}
              key={`cell-${index}`}
              fill={actualData[index % actualData.length].color}
            />
          ))}
        </Pie>
      </RePieChart>
    </ResponsiveContainer>
  );
};

export default React.memo(PieChart);
